// Package Imports
import React from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Input } from "antd";

// Components
import AuthForm from "../../../components/forms/AuthForm";
import { BodyOne, HeadingOne } from "../../../components/typography";
import BlueButton from "../../../components/buttons/BlueButton";
import CountrySelector from "../../../components/inputs/CountrySelector";
import { useAuthStore } from "../../../store";
import SupportBlock from "../../../components/info/SupportBlock";
import bugfenderHelper from "../../../services/bugfender";

/**
 * Company name and registration country after account verification.
 * @param {{ nextStep: Function, prevStep: Function, formChangeHandler: Function }} props
 */
const CompanyDetailsForm = ({ nextStep, prevStep, formChangeHandler }) => {
  const navigate = useNavigate();
  const singUpState = useAuthStore((s) => s.signUpInputs)
  const { handleSubmit, control, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    try {
      await formChangeHandler(data, "company");
      nextStep();
    } catch (error) {
      bugfenderHelper.error(error);
      console.error(error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AuthForm>
        <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
          Company Details
        </HeadingOne>
        <div className="flex flex-col">
          <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>Registered Name</BodyOne>
          <Controller
            name="org_name"
            control={control}
            defaultValue={singUpState?.org_name || ""}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Input size="large" placeholder="Enter your registered company name here" onChange={onChange} value={value} />
            )}
          />
          {errors.org_name && (
            <BodyOne className="text-red-600">
              Please enter your registered company name
            </BodyOne>
          )}
        </div>
        <div className="flex flex-col mt-4">
          <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>Trading Name</BodyOne>
          <Controller
            name="org_trading_name"
            control={control}
            defaultValue={singUpState?.org_trading_name || ""}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Input size="large" placeholder="Enter your company trading name here" onChange={onChange} value={value} />
            )}
          />
          {errors.org_trading_name && (
            <BodyOne className="text-red-600">
              Please enter your company trading name
            </BodyOne>
          )}
        </div>
        <div className="flex flex-col mt-4">
          <BodyOne className="mb-1.5 text-slate-600 font-medium" isRequired={true}>Country of Registration</BodyOne>
          <Controller
            name="registration_country"
            control={control}
            defaultValue={singUpState?.registration_country || "South Africa"}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <CountrySelector onChange={onChange} value={value} />
            )}
          />
          {errors.registration_country && (
            <BodyOne className="text-red-600">
              Please enter your country of registration
            </BodyOne>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <BlueButton
            className="bg-white border border-[#0458f6] text-[#0458f6] flex justify-center items-center hover:bg-[#0458f6] hover:text-white"
            type="button"
            style={{ color: "#0458F6" }}
            onClick={() => prevStep()}
          >
            Back
          </BlueButton>
          <BlueButton
            className="text-white flex justify-center items-center py-2.5 font-semibold hover:text-white"
            type="submit"
          >
            Proceed
          </BlueButton>
        </div>
        <p className="text-center mt-5 mb-0 text-xs leading-5 text-slate-500 font-poppins">
          By continuing you agree to our Terms of Service and Privacy Policy
        </p>
        <p className="text-center mt-3 mb-0 text-xs leading-5 text-slate-500 font-poppins">
          Already have an account?{" "}
          <span
            className="text-[#0458f6] font-medium cursor-pointer hover:underline"
            onClick={() => navigate("/login/mobile-app/MzRlNTgzNGNkMzVka3J2anAwcGpkanAxYmM=")}
          >
            Login
          </span>
        </p>
        <div className="auth-footer-rule" />
        <SupportBlock area="sign-up-company-details" tailwindProps="mt-0" />
      </AuthForm>
    </form>
  );
};

export default CompanyDetailsForm;
